import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { MaleIcon, FemaleIcon } from '../../components/common/GenderIcon'

const UserTournamentResult = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [summary, setSummary] = useState([])
  const [totalTeams, setTotalTeams] = useState({ M: 0, F: 0 })
  const [hasParticipated, setHasParticipated] = useState(false)
  const [userResults, setUserResults] = useState([])
  const [userTeamName, setUserTeamName] = useState('')
  const [teamMembers, setTeamMembers] = useState([])
  const [genderFilter, setGenderFilter] = useState('all')
  const [allTeams, setAllTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchResults()
  }, [eventId])

  const fetchResults = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get(`/user/events/${eventId}/my-results`)
      if (response.data.success) {
        setEvent(response.data.data.event)
        setSummary(response.data.data.summary || [])
        setTotalTeams(response.data.data.total_teams || { M: 0, F: 0 })
        setHasParticipated(response.data.data.has_participated || false)
        setUserResults(response.data.data.user_results || [])
        setUserTeamName(response.data.data.user_team_name || '')
        setTeamMembers(response.data.data.team_members || [])
        setAllTeams(response.data.data.all_teams || [])
      } else {
        setError(response.data.message || 'Failed to load results')
      }
    } catch (error) {
      console.error('Error fetching results:', error)
      const msg = error.response?.data?.message || error.response?.statusText || 'Failed to load tournament results. Please try again.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const positionLabels = {
    'winner': 'Winner',
    'joint-winner': 'Joint Winner',
    'runner-up': 'Runner Up',
    'joint-runner-up': 'Joint Runner Up',
    '3rd-place': '3rd Place',
    'joint-3rd-place': 'Joint 3rd Place',
    '4th-place': '4th Place',
    '5th-place': '5th Place'
  }

  const positionBadge = (position) => {
    const config = {
      'winner': { bg: '#fef3c7', color: '#92400e', border: '#fbbf24', icon: 'fas fa-trophy' },
      'joint-winner': { bg: '#fef3c7', color: '#92400e', border: '#fbbf24', icon: 'fas fa-trophy' },
      'runner-up': { bg: '#f3f4f6', color: '#374151', border: '#9ca3af', icon: 'fas fa-medal' },
      'joint-runner-up': { bg: '#f3f4f6', color: '#374151', border: '#9ca3af', icon: 'fas fa-medal' },
      '3rd-place': { bg: '#fef2f2', color: '#92400e', border: '#cd7f32', icon: 'fas fa-award' },
      'joint-3rd-place': { bg: '#fef2f2', color: '#92400e', border: '#cd7f32', icon: 'fas fa-award' }
    }
    const c = config[position] || { bg: '#f0f9ff', color: '#1e40af', border: '#93c5fd', icon: 'fas fa-flag' }
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700',
        background: c.bg, color: c.color, border: `1px solid ${c.border}`
      }}>
        <i className={c.icon} style={{ fontSize: '0.7rem' }}></i>
        {positionLabels[position] || position}
      </span>
    )
  }

  const getRoleBadge = (role) => {
    const config = {
      'PLAYER': { bg: '#dbeafe', color: '#1e40af', label: 'Player' },
      'COACH': { bg: '#fef3c7', color: '#92400e', label: 'Coach' },
      'MANAGER': { bg: '#e0e7ff', color: '#3730a3', label: 'Manager' },
      'OFFICIAL': { bg: '#fce7f3', color: '#9d174d', label: 'Official' }
    }
    const c = config[role] || config['PLAYER']
    return (
      <span style={{
        padding: '2px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
        background: c.bg, color: c.color
      }}>{c.label}</span>
    )
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', background: '#f3f4f6', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', padding: '4rem 2rem', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#c00f15', marginBottom: '1rem' }}></i>
          <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>Loading results...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', background: '#f3f4f6', minHeight: '100vh' }}>
        <div style={{
          background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 50%, #c00f15 100%)',
          padding: '1.5rem 2rem', color: 'white', borderRadius: '12px', marginBottom: '1.5rem',
          display: 'flex', alignItems: 'center', gap: '1rem'
        }}>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700', color: '#ffffff', flex: 1 }}>
            <i className="fas fa-trophy" style={{ marginRight: '12px' }}></i>Tournament Results
          </h1>
          <button onClick={() => navigate(-1)} style={{
            padding: '8px 20px', background: 'white', border: 'none',
            borderRadius: '8px', cursor: 'pointer', color: '#c00f15', fontWeight: '600', fontSize: '0.9rem',
            display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', whiteSpace: 'nowrap'
          }}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '4rem 2rem', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#ef4444', marginBottom: '1rem', display: 'block' }}></i>
          <h3 style={{ color: '#1f2937', marginBottom: '0.5rem' }}>Error Loading Results</h3>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{error}</p>
          <button onClick={() => fetchResults()} style={{
            padding: '10px 24px', background: 'linear-gradient(135deg, #c00f15, #a00d12)', border: 'none',
            borderRadius: '8px', color: 'white', fontWeight: '600', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem'
          }}>
            <i className="fas fa-redo"></i> Try Again
          </button>
        </div>
      </div>
    )
  }

  const genderOf = (m) => (['M', 'Male', 'male'].includes(m.gender) ? 'male' : 'female')
  const gmatch = (m) => genderFilter === 'all' || genderOf(m) === genderFilter
  const players = teamMembers.filter(m => m.member_role === 'PLAYER' && gmatch(m))
  const coaches = teamMembers.filter(m => m.member_role === 'COACH' && gmatch(m))
  const managers = teamMembers.filter(m => m.member_role === 'MANAGER' && gmatch(m))
  const officials = teamMembers.filter(m => m.member_role === 'OFFICIAL' && gmatch(m))

  return (
    <div style={{ padding: '1.5rem 2rem', background: '#f3f4f6', minHeight: '100vh', width: '100%' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 50%, #c00f15 100%)',
        padding: '1.5rem 2rem', color: 'white', borderRadius: '12px', marginBottom: '1.5rem',
        display: 'flex', alignItems: 'center', gap: '1rem'
      }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.75rem', fontWeight: '700', color: '#ffffff' }}>
            <i className="fas fa-trophy" style={{ marginRight: '12px' }}></i>Tournament Results
          </h1>
          <p style={{ margin: 0, fontSize: '1rem', color: '#ffffff' }}>{event?.event_name}</p>
        </div>
        <button onClick={() => navigate(-1)} style={{
          padding: '8px 20px', background: 'white', border: 'none',
          borderRadius: '8px', cursor: 'pointer', color: '#c00f15', fontWeight: '600', fontSize: '0.9rem',
          display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', whiteSpace: 'nowrap'
        }}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Event Info */}
      <div style={{
        background: 'white', borderRadius: '12px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', color: '#4b5563' }}>
          <i className="fas fa-map-marker-alt" style={{ color: '#c00f15' }}></i>
          {event?.event_venue}{event?.event_city ? `, ${event.event_city}` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', color: '#4b5563' }}>
          <i className="fas fa-calendar-alt" style={{ color: '#c00f15' }}></i>
          {event?.formatted_dates}
        </div>
        {userTeamName && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', color: '#c00f15', fontWeight: '600' }}>
            <i className="fas fa-flag" style={{ color: '#c00f15' }}></i>Your Team: {userTeamName}
          </div>
        )}
      </div>

      {/* ===================== ALL PARTICIPATING TEAMS ===================== */}
      {allTeams.length > 0 && (
        <div style={{
          background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          marginBottom: '1.5rem', overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
            padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-users" style={{ color: '#c00f15' }}></i>
              All Participating Teams
              <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#6b7280' }}>({allTeams.length} teams)</span>
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(to right, #c00f15, #a00d12, #c00f15)' }}>
                  <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent', width: '50px' }}>#</th>
                  <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Team / State</th>
                  <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Position</th>
                </tr>
              </thead>
              <tbody>
                {allTeams.map((team, index) => {
                  const isMyTeam = team.team_name === userTeamName
                  const hasPosition = team.positions && team.positions.length > 0
                  return (
                    <tr key={team.team_id} style={{
                      background: isMyTeam ? '#fef2f2' : (index % 2 === 0 ? 'white' : '#fafbfc'),
                      borderBottom: '1px solid #e5e7eb',
                      borderLeft: isMyTeam ? '4px solid #c00f15' : '4px solid transparent'
                    }}>
                      <td style={{ padding: '10px 16px', color: '#6b7280', fontSize: '0.9rem' }}>{index + 1}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {isMyTeam && <i className="fas fa-star" style={{ color: '#f59e0b', fontSize: '0.85rem' }}></i>}
                          <div>
                            <div style={{ fontWeight: isMyTeam ? '700' : '600', color: isMyTeam ? '#c00f15' : '#1f2937', fontSize: '0.95rem' }}>
                              {team.team_name}
                            </div>
                            {team.state_name && (
                              <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{team.state_name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        {hasPosition ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                            {team.positions.map((p, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {positionBadge(p.position)}
                                <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>({p.gender})</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span style={{
                            padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
                            background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0'
                          }}>
                            <i className="fas fa-check-circle" style={{ marginRight: '4px', fontSize: '0.7rem' }}></i>
                            Participant
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===================== RESULT SUMMARY TABLE ===================== */}
      {summary.length > 0 && (
        summary.map((catData, catIndex) => {
          const hasMen = catData.men && catData.men.length > 0
          const hasWomen = catData.women && catData.women.length > 0

          const allPositions = []
          const posOrder = ['winner', 'joint-winner', 'runner-up', 'joint-runner-up', '3rd-place', 'joint-3rd-place', '4th-place', '5th-place']
          posOrder.forEach(pos => {
            const menHas = catData.men?.some(r => r.position === pos)
            const womenHas = catData.women?.some(r => r.position === pos)
            if (menHas || womenHas) allPositions.push(pos)
          })

          return (
            <div key={catIndex} style={{
              background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              marginBottom: '1.5rem', overflow: 'hidden'
            }}>
              <div style={{
                background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
                padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-medal" style={{ color: '#c00f15' }}></i>
                  Position Summary {summary.length > 1 ? `- ${catData.category}` : ''}
                </h3>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(to right, #c00f15, #a00d12, #c00f15)' }}>
                      <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'left', fontSize: '0.85rem', fontWeight: '600', background: 'transparent' }}></th>
                      {hasMen && (
                        <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'center', fontSize: '0.95rem', fontWeight: '700', background: 'transparent' }}>
                          <MaleIcon style={{ marginRight: '6px' }} />Men
                        </th>
                      )}
                      {hasWomen && (
                        <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'center', fontSize: '0.95rem', fontWeight: '700', background: 'transparent' }}>
                          <FemaleIcon style={{ marginRight: '6px' }} />Women
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: '#f0fdf4' }}>
                      <td style={{ padding: '10px 16px', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Total Participating Teams:</td>
                      {hasMen && <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '700', color: '#166534', borderBottom: '1px solid #e5e7eb', fontSize: '1.1rem' }}>{totalTeams.M || 0}</td>}
                      {hasWomen && <td style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '700', color: '#166534', borderBottom: '1px solid #e5e7eb', fontSize: '1.1rem' }}>{totalTeams.F || 0}</td>}
                    </tr>
                    {allPositions.map((pos, posIndex) => {
                      const posIcon = {
                        'winner': { icon: 'fas fa-trophy', color: '#f59e0b' },
                        'joint-winner': { icon: 'fas fa-trophy', color: '#f59e0b' },
                        'runner-up': { icon: 'fas fa-medal', color: '#9ca3af' },
                        'joint-runner-up': { icon: 'fas fa-medal', color: '#9ca3af' },
                        '3rd-place': { icon: 'fas fa-award', color: '#cd7f32' },
                        'joint-3rd-place': { icon: 'fas fa-award', color: '#cd7f32' }
                      }[pos]
                      const menTeams = hasMen ? catData.men.filter(r => r.position === pos).map(r => r.team_name) : []
                      const womenTeams = hasWomen ? catData.women.filter(r => r.position === pos).map(r => r.team_name) : []

                      const renderTeamCell = (teams) => (
                        <td style={{ padding: '10px 16px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                          {teams.length > 0 ? teams.map((team, i) => (
                            <span key={i} style={{
                              display: 'inline-block',
                              fontWeight: team === userTeamName ? '700' : '500',
                              color: team === userTeamName ? '#c00f15' : '#1f2937',
                              background: team === userTeamName ? '#fef2f2' : 'transparent',
                              padding: team === userTeamName ? '2px 12px' : '2px 4px',
                              borderRadius: '6px',
                              border: team === userTeamName ? '1px solid #fca5a5' : 'none'
                            }}>
                              {team === userTeamName && <i className="fas fa-star" style={{ color: '#f59e0b', marginRight: '4px', fontSize: '0.7rem' }}></i>}
                              {team}{i < teams.length - 1 ? ', ' : ''}
                            </span>
                          )) : '-'}
                        </td>
                      )

                      return (
                        <tr key={pos} style={{ background: posIndex % 2 === 0 ? 'white' : '#fafbfc' }}>
                          <td style={{ padding: '10px 16px', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                            {posIcon && <i className={posIcon.icon} style={{ color: posIcon.color, marginRight: '8px' }}></i>}
                            {positionLabels[pos] || pos}:
                          </td>
                          {hasMen && renderTeamCell(menTeams)}
                          {hasWomen && renderTeamCell(womenTeams)}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )
        })
      )}

      {/* No results message */}
      {summary.length === 0 && allTeams.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1.5rem' }}>
          <i className="fas fa-info-circle" style={{ fontSize: '4rem', color: '#9ca3af', marginBottom: '1rem', display: 'block' }}></i>
          <h3 style={{ color: '#374151', marginBottom: '0.5rem' }}>No Results Available</h3>
          <p style={{ color: '#6b7280' }}>Results for this tournament have not been announced yet.</p>
        </div>
      )}

      {/* ===================== YOUR TEAM MEMBERS ===================== */}
      {hasParticipated && teamMembers.length > 0 && (
        <div style={{
          background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
            padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
          }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-id-card" style={{ color: '#c00f15' }}></i>
              Your Team Members - {userTeamName}
              <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#6b7280' }}>({teamMembers.filter(gmatch).length} members)</span>
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[{ key: 'all', label: 'All' }, { key: 'male', label: 'Male' }, { key: 'female', label: 'Female' }].map(opt => {
                const active = genderFilter === opt.key
                return (
                  <button
                    key={opt.key}
                    onClick={() => setGenderFilter(opt.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '6px 14px', borderRadius: '8px', cursor: 'pointer',
                      fontSize: '0.85rem', fontWeight: '600',
                      border: active ? 'none' : '1px solid #d1d5db',
                      background: active ? '#c00f15' : '#fff',
                      color: active ? '#fff' : '#374151',
                    }}
                  >
                    {opt.key === 'male' && <MaleIcon />}
                    {opt.key === 'female' && <FemaleIcon />}
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(to right, #c00f15, #a00d12, #c00f15)' }}>
                  <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>#</th>
                  <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Reg ID</th>
                  <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Name</th>
                  <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Gender</th>
                  <th style={{ padding: '10px 16px', color: '#fff', textAlign: 'center', fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', background: 'transparent' }}>Role</th>
                </tr>
              </thead>
              <tbody>
                {/* Players */}
                {players.length > 0 && (
                  <tr style={{ background: '#eff6ff' }}>
                    <td colSpan="5" style={{ padding: '8px 16px', fontWeight: '700', color: '#1e40af', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb' }}>
                      <i className="fas fa-running" style={{ marginRight: '6px' }}></i>Players ({players.length})
                    </td>
                  </tr>
                )}
                {players.map((m, i) => (
                  <tr key={`p-${m.id}`} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc', borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 16px', color: '#6b7280', fontSize: '0.9rem' }}>{i + 1}</td>
                    <td style={{ padding: '8px 16px', color: '#4b5563', fontSize: '0.9rem', fontFamily: 'monospace' }}>{m.reg_id || '-'}</td>
                    <td style={{ padding: '8px 16px', fontWeight: '600', color: '#1f2937', fontSize: '0.9rem' }}>{m.full_name}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'center', color: '#4b5563', fontSize: '0.9rem' }}>
                      {['M', 'Male', 'male'].includes(m.gender) ? 'Male' : 'Female'}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>{getRoleBadge(m.member_role)}</td>
                  </tr>
                ))}

                {/* Coaches */}
                {coaches.length > 0 && (
                  <tr style={{ background: '#fefce8' }}>
                    <td colSpan="5" style={{ padding: '8px 16px', fontWeight: '700', color: '#92400e', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb' }}>
                      <i className="fas fa-chalkboard-teacher" style={{ marginRight: '6px' }}></i>Coaches ({coaches.length})
                    </td>
                  </tr>
                )}
                {coaches.map((m, i) => (
                  <tr key={`c-${m.id}`} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc', borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 16px', color: '#6b7280', fontSize: '0.9rem' }}>{i + 1}</td>
                    <td style={{ padding: '8px 16px', color: '#4b5563', fontSize: '0.9rem', fontFamily: 'monospace' }}>{m.reg_id || '-'}</td>
                    <td style={{ padding: '8px 16px', fontWeight: '600', color: '#1f2937', fontSize: '0.9rem' }}>{m.full_name}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'center', color: '#4b5563', fontSize: '0.9rem' }}>
                      {['M', 'Male', 'male'].includes(m.gender) ? 'Male' : 'Female'}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>{getRoleBadge(m.member_role)}</td>
                  </tr>
                ))}

                {/* Managers */}
                {managers.length > 0 && (
                  <tr style={{ background: '#eef2ff' }}>
                    <td colSpan="5" style={{ padding: '8px 16px', fontWeight: '700', color: '#3730a3', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb' }}>
                      <i className="fas fa-user-tie" style={{ marginRight: '6px' }}></i>Managers ({managers.length})
                    </td>
                  </tr>
                )}
                {managers.map((m, i) => (
                  <tr key={`m-${m.id}`} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc', borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 16px', color: '#6b7280', fontSize: '0.9rem' }}>{i + 1}</td>
                    <td style={{ padding: '8px 16px', color: '#4b5563', fontSize: '0.9rem', fontFamily: 'monospace' }}>{m.reg_id || '-'}</td>
                    <td style={{ padding: '8px 16px', fontWeight: '600', color: '#1f2937', fontSize: '0.9rem' }}>{m.full_name}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'center', color: '#4b5563', fontSize: '0.9rem' }}>
                      {['M', 'Male', 'male'].includes(m.gender) ? 'Male' : 'Female'}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>{getRoleBadge(m.member_role)}</td>
                  </tr>
                ))}

                {/* Officials */}
                {officials.length > 0 && (
                  <tr style={{ background: '#fdf2f8' }}>
                    <td colSpan="5" style={{ padding: '8px 16px', fontWeight: '700', color: '#9d174d', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb' }}>
                      <i className="fas fa-id-badge" style={{ marginRight: '6px' }}></i>Officials ({officials.length})
                    </td>
                  </tr>
                )}
                {officials.map((m, i) => (
                  <tr key={`o-${m.id}`} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc', borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 16px', color: '#6b7280', fontSize: '0.9rem' }}>{i + 1}</td>
                    <td style={{ padding: '8px 16px', color: '#4b5563', fontSize: '0.9rem', fontFamily: 'monospace' }}>{m.reg_id || '-'}</td>
                    <td style={{ padding: '8px 16px', fontWeight: '600', color: '#1f2937', fontSize: '0.9rem' }}>{m.full_name}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'center', color: '#4b5563', fontSize: '0.9rem' }}>
                      {['M', 'Male', 'male'].includes(m.gender) ? 'Male' : 'Female'}
                    </td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>{getRoleBadge(m.member_role)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserTournamentResult
